/**
 * The Flow A disconnect ladder — flap-avoidance plus the two escalation
 * timers — as one reusable object, shared by the map stream
 * (:func:`@/map/use-sse`) and the system-status stream
 * (:func:`@/system-status/use-system-status-stream`).
 *
 * # Why a module and not two copies
 *
 * The map hook carried these timers inline from Stream A PR 2 onward, and the
 * status hook reused only the flap constant. When the status stream gained a
 * reconnect ladder of its own (REQ-STATUS-062 as amended 2026-10-19 — a CLOSED
 * EventSource is no longer assumed to be an auth failure), the choice was a
 * second copy of the same three timers or one object both hooks drive. CLAUDE.md
 * §4.4 settles it. The object also closes a latent defect the inline copy had:
 * every post-flap ``onerror`` (the browser re-fires it on each failed native
 * retry) overwrote the timer handles without clearing the previous set, so a
 * recovery cleared only the latest and the earliest still fired ``disconnected``
 * over a live stream. One outage now owns exactly one set of timers.
 *
 * # The ladder
 *
 * | Elapsed since the first drop | Hook fired         | Flow A frame |
 * |------------------------------|--------------------|--------------|
 * | ``FLAP_AVOIDANCE_MS``        | ``onReconnecting`` | Frame 2      |
 * | ``DISCONNECTED_THRESHOLD_MS``| ``onDisconnected`` | Frame 3      |
 * | ``FAIL_STOP_THRESHOLD_MS``   | ``onFailStop``     | Frame 5      |
 *
 * The clock starts at the **first** :func:`DisconnectLadder.drop` of an outage
 * and every later ``drop`` inside the same outage is a no-op, so a drop at
 * ``t=0`` and a retry failure at ``t=3 s`` escalate at ``30 s`` and ``5 min``,
 * not ``33 s`` and ``5 min 3 s``. :func:`DisconnectLadder.clear` ends the outage
 * (the transport recovered, or the owner is tearing down) and cancels every
 * pending hook.
 *
 * The ladder is **display-side**: it never opens, closes or retries a
 * connection. The map stream relies on the browser's native EventSource
 * retry; the status stream schedules its own re-opens and uses
 * :attr:`DisconnectLadder.exhausted` as the bound on them.
 *
 * Cross-references:
 *   - docs/engineering/detail/storyboards/flows/A-sse-recovery/README.md
 *     §"Connection-state machine", §"Frame 2" / §"Frame 3" / §"Frame 5".
 *   - REQ-UI-008 (automatic reconnect); REQ-STATUS-062 (the status stream's
 *     bounded reconnect).
 */

/**
 * Threshold timings, in milliseconds, for the Flow A state-machine ladder.
 * Exported for unit-test reach so tests can advance fake timers past each
 * boundary without duplicating literals. Re-exported unchanged from
 * :file:`@/map/use-sse`, the import path every existing consumer uses.
 */
export const SSE_TIMINGS = {
  /**
   * Flap-avoidance delay after ``onerror`` before transitioning
   * ``live → reconnecting``. Per Flow A's "Very brief disconnect
   * (< 1 s). UI does not transition to RECONNECTING" — a fast
   * reconnect cancels this timer.
   */
  FLAP_AVOIDANCE_MS: 800,
  /**
   * Elapsed-time threshold to transition
   * ``reconnecting → disconnected``. Per Flow A Frame 3.
   */
  DISCONNECTED_THRESHOLD_MS: 30_000,
  /**
   * Elapsed-time threshold to transition
   * ``disconnected → fail-stop``. Per Flow A Frame 5.
   */
  FAIL_STOP_THRESHOLD_MS: 5 * 60 * 1_000,
} as const;

/** The timing table a ladder runs on; :data:`SSE_TIMINGS` unless a test narrows it. */
export type DisconnectLadderTimings = {
  readonly [K in keyof typeof SSE_TIMINGS]: number;
};

/** What the owner does at each rung. Only the first rung is mandatory. */
export interface DisconnectLadderHooks {
  /** The flap window elapsed without a recovery: the drop is real. */
  onReconnecting: () => void;
  /** Still down at the disconnected threshold (Flow A Frame 3). */
  onDisconnected?: () => void;
  /** Still down at the fail-stop threshold (Flow A Frame 5). */
  onFailStop?: () => void;
}

/** One outage's worth of timers; see the module docstring. */
export interface DisconnectLadder {
  /**
   * Register a transport error.
   *
   * @returns ``true`` when this call opened a new outage (the clock started
   *     now); ``false`` when an outage is already being timed and this drop
   *     is the browser re-firing ``onerror`` inside it.
   */
  drop: () => boolean;
  /** End the outage: cancel every pending rung. Idempotent. */
  clear: () => void;
  /** ``true`` from the first ``drop`` of an outage until ``clear``. */
  readonly active: boolean;
  /** ``true`` once the fail-stop rung has fired in the current outage. */
  readonly exhausted: boolean;
}

/**
 * Build a disconnect ladder over ``hooks``.
 *
 * @param hooks - The per-rung callbacks.
 * @param timings - The threshold table; defaults to :data:`SSE_TIMINGS`.
 * @returns A ladder that is inert until its first :func:`DisconnectLadder.drop`.
 */
export function createDisconnectLadder(
  hooks: DisconnectLadderHooks,
  timings: DisconnectLadderTimings = SSE_TIMINGS,
): DisconnectLadder {
  let flapTimer: ReturnType<typeof setTimeout> | null = null;
  let disconnectedTimer: ReturnType<typeof setTimeout> | null = null;
  let failStopTimer: ReturnType<typeof setTimeout> | null = null;
  let active = false;
  let exhausted = false;

  const clear = (): void => {
    if (flapTimer !== null) {
      clearTimeout(flapTimer);
      flapTimer = null;
    }
    if (disconnectedTimer !== null) {
      clearTimeout(disconnectedTimer);
      disconnectedTimer = null;
    }
    if (failStopTimer !== null) {
      clearTimeout(failStopTimer);
      failStopTimer = null;
    }
    active = false;
    exhausted = false;
  };

  const drop = (): boolean => {
    if (active) return false;
    active = true;
    exhausted = false;
    flapTimer = setTimeout(() => {
      flapTimer = null;
      hooks.onReconnecting();
    }, timings.FLAP_AVOIDANCE_MS);
    // Both escalation rungs are measured from the drop itself, not from the
    // flap rung, so the storyboard's "30 s" and "5 min" are wall-clock from
    // the moment the transport went away.
    disconnectedTimer = setTimeout(() => {
      disconnectedTimer = null;
      hooks.onDisconnected?.();
    }, timings.DISCONNECTED_THRESHOLD_MS);
    failStopTimer = setTimeout(() => {
      failStopTimer = null;
      exhausted = true;
      hooks.onFailStop?.();
    }, timings.FAIL_STOP_THRESHOLD_MS);
    return true;
  };

  return {
    drop,
    clear,
    get active() {
      return active;
    },
    get exhausted() {
      return exhausted;
    },
  };
}
